import { Form, Input } from "decorator-form";

import InputType from "common/form/InputType";
import Loading from "common/fragments/Loading";
import { BlockView } from "common/views/block/BlockView.view";
import { ButtonView } from "common/views/button/Button.view";
import { CardVariant, CardView } from "common/views/card/Card.view";
import HeadingView from "common/views/heading/Heading.view";
import { ImageVariant, ImageView } from "common/views/image/Image.view";
import PageLinkView from "common/views/pageLink/PageLink.view";
import { RowsView } from "common/views/rows/Rows.view";
import { ITab, TabsView } from "common/views/tab/Tabs.view";
import TypographyView from "common/views/typographyView/Typography.view";
import Query from "core/api/QueryComponent";
import Label from "core/components/label/Label";
import UrlUtil from "core/util/UrlUtil";
import constants from "panels/kb/constants";
import { Article, ChildCategory } from "../entities";
import { CategoryTypeRegistry, ICategoryType } from "../ICategoryType";
import HtmlDefault from "common/fragments/HtmlDefault";

@Form({ title: "Card List Config" })
class CardListConfig {
	@Input({ type: InputType.CHECKBOX, title: "Show Title" })
	public showTitle: boolean;

	@Input({ type: InputType.CHECKBOX, title: "Show Description" })
	public showDescription: boolean;

	@Input({
		type: InputType.CHECKBOX,
		title: "Show Child Categories In Popovers"
	})
	public childrenInPopover: boolean;
}

const CardListCategory: ICategoryType<CardListConfig> = {
	type: "list:cards",
	label: "Card List",
	icon: "grid-view",

	render: ({ category }) => {
		const tabs: ITab[] = [];

		if (category.childrenCount && category.childrenCount.count) {
			tabs.push({
				key: "children",
				label: <Label k="category.children" v="Categories" />,
				content: (
					<Query.Many
						entity={ChildCategory}
						args={{
							parentId: category.id,
							pageSize: category.childrenCount.count,
							sort: "sortIndex"
						}}
					>
						{args => {
							if (args.loading) {
								return <Loading />;
							}

							if (!args.data || !args.data.length) {
								return null;
							}

							return (
								<RowsView
									viewKey="category.children.rows"
									skeleton={true}
								>
									{args.data.map(cc => {
										return (
											<PageLinkView
												key={cc.id}
												viewKey="category.link"
												params={{ categoryId: cc.id }}
												preserveParams={false}
												breadcrumbs={(
													target,
													existingBreadcrumbs
												) => {
													const categoryKey =
														"category:" +
														category.id;
													return [
														...existingBreadcrumbs.filter(
															c =>
																c.key !==
																categoryKey
														),
														{
															key: categoryKey,
															label:
																category.name,
															target: {
																pathname: UrlUtil.getCurrentPath(),
																search: UrlUtil.getSearch()
															}
														},
														{
															key:
																"category:" +
																cc.id,
															label: cc.name,
															target
														}
													];
												}}
											>
												<CardView
													viewKey="card"
													variant={
														CardVariant.DEFAULT
													}
													interactive={true}
												>
													<ImageView
														viewKey="card.icon"
														variant={
															ImageVariant.THUMBNAIL
														}
														src={cc.iconUrl}
													/>
													<BlockView
														viewKey="card.body"
														style={{
															position: "relative"
														}}
													>
														<HeadingView viewKey="card.name">
															{cc.name}
														</HeadingView>
													</BlockView>
												</CardView>
											</PageLinkView>
										);
									})}
								</RowsView>
							);
						}}
					</Query.Many>
				)
			});
		}

		if (category.articleCount && category.articleCount.count) {
			tabs.push({
				key: "articles",
				label: <Label k="category.articles" v="Articles" />,
				content: (
					<Query.Many
						entity={Article}
						args={{
							categoryIds: [category.id],
							sort: "-a.publishedOn",
							pageSize: 12 // only load one page worth, link to search page for the rest
						}}
					>
						{args => {
							if (args.loading) {
								return <Loading />;
							}

							if (!args.data || !args.data.length) {
								return null;
							}

							return (
								<div>
									<RowsView viewKey="category.articles.rows">
										{args.data.map(article => {
											return (
												<PageLinkView
													key={article.id}
													viewKey="article.link"
													params={{
														[constants.PARAM_ARTICLE_ID]:
															article.id,
														[constants.PARAM_CATEGORY_ID]:
															category.id,
														[constants.PARAM_KB_ID]:
															category
																.knowledgeBase
																.id
													}}
													preserveParams={false}
													breadcrumbs={(
														target,
														existingBreadcrumbs
													) => {
														const categoryKey =
															"category:" +
															category.id;
														return [
															...existingBreadcrumbs.filter(
																c =>
																	c.key !==
																	categoryKey
															),
															{
																key: categoryKey,
																label:
																	category.name,
																target: {
																	pathname: UrlUtil.getCurrentPath(),
																	search: UrlUtil.getSearch()
																}
															},
															{
																key:
																	"article:" +
																	article.id,
																label:
																	article.displayName ||
																	article.name,
																target
															}
														];
													}}
												>
													<CardView
														viewKey="card"
														variant={
															CardVariant.DEFAULT
														}
														interactive={true}
													>
														<ImageView
															viewKey="card.icon"
															variant={
																ImageVariant.THUMBNAIL
															}
															src={
																article.iconUrl
															}
														/>
														<BlockView
															viewKey="card.body"
															style={{
																position:
																	"relative"
															}}
														>
															<HeadingView viewKey="card.name">
																{article.displayName ||
																	article.name}
															</HeadingView>
														</BlockView>
													</CardView>
												</PageLinkView>
											);
										})}
									</RowsView>
									<BlockView viewKey="articles.actions">
										<PageLinkView
											viewKey="viewall.link"
											preserveParams={false}
											params={{
												[constants.PARAM_CATEGORY_IDS]:
													category.id
											}}
										>
											<ButtonView
												viewKey="viewall.button"
												span={true}
											>
												<Label
													k="browse.all"
													v="Browse All"
												/>
											</ButtonView>
										</PageLinkView>
									</BlockView>
								</div>
							);
						}}
					</Query.Many>
				)
			});
		}

		return (
			<div>
				<HeadingView viewKey="category.name">
					{category.name}
				</HeadingView>
				{!!category.description && (
					<TypographyView viewKey="category.description">
						<HtmlDefault html={category.description} />
					</TypographyView>
				)}
				<TabsView viewKey="tabs" tabs={tabs} />
			</div>
		);
	}
};

export default CategoryTypeRegistry.putObject(CardListCategory);



// WEBPACK FOOTER //
// ./src/panels/kb/category/categoryTypes/CardListCategory.reg.tsx